import { Box, ListItem, ListItemButton, ListItemIcon, ListItemText, Stack } from '@mui/material';
import { CalendarCheckIcon, ChartBarIcon, DatabaseIcon, FactoryIcon, GearIcon, HouseIcon, ShieldStarIcon, UserCircleGearIcon, UsersIcon } from '@phosphor-icons/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIsInternalUser } from '../utils/useIsInternalUser.js';

export const SIDEBAR_WIDTH = 240;

type NavItem = {
  path: string;
  label: string;
  icon: React.ComponentType<{ size?: number; weight?: 'regular' | 'fill' }>;
  exact?: boolean;
  adminOnly?: boolean;
  internalOnly?: boolean;
};

const NAV_ITEMS: NavItem[] = [
  { path: '/agent', label: 'Home', icon: HouseIcon, exact: true },
  { path: '/agent/contacts', label: 'Contacts', icon: UsersIcon },
  { path: '/agent/reminders', label: 'Reminders', icon: CalendarCheckIcon, internalOnly: true },
  { path: '/agent/insights', label: 'Insights', icon: ChartBarIcon },
  { path: '/agent/nexus', label: 'Nexus', icon: DatabaseIcon },
  { path: '/agent/foundry', label: 'Foundry', icon: FactoryIcon },
  { path: '/agent/settings', label: 'Integrations', icon: GearIcon },
  { path: '/agent/account', label: 'Account', icon: UserCircleGearIcon },
  { path: '/agent/admin/clients', label: 'Admin', icon: ShieldStarIcon, adminOnly: true },
];

export default function AgentSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isInternalUser, isFlaggedForReminders } = useIsInternalUser();

  // Filter nav items based on admin and internal user status
  const visibleNavItems = NAV_ITEMS.filter(item => {
    if (item.adminOnly && !isInternalUser) return false;
    if (item.internalOnly) {
      if (item.path === '/agent/reminders') return isFlaggedForReminders;
      return isInternalUser;
    }
    return true;
  });

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack spacing={0.5} sx={{ px: 2.5, py: 2, flex: 1 }}>
        {visibleNavItems.map((item) => {
          const Icon = item.icon;
          const isSelected = item.exact
            ? location.pathname === item.path
            : location.pathname === item.path || location.pathname.startsWith(item.path + '/');

          return (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                onClick={() => navigate(item.path)}
                sx={{
                  borderRadius: 3,
                  py: 1.5,
                  px: 2,
                  backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.25)' : 'transparent',
                  border: '1px solid transparent',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.15)',
                    transform: 'translateX(2px)',
                  },
                  '&:active': {
                    transform: 'translateX(3px)',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 44,
                    color: 'white',
                    opacity: isSelected ? 1 : 0.85,
                    transition: 'opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <Icon size={24} weight={isSelected ? 'fill' : 'regular'} />
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  slotProps={{
                    primary: {
                      sx: {
                        color: 'white',
                        fontWeight: isSelected ? 600 : 500,
                        fontSize: '1rem',
                        letterSpacing: '-0.01em',
                        opacity: isSelected ? 1 : 0.85,
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      },
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </Stack>
    </Box>
  );
}
